import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useExpertContext } from '../context/ExpertContext';
import { useLanguage } from '../context/LanguageContext';

const { FiX, FiMapPin, FiUser, FiStar, FiChevronRight } = FiIcons;

const ExpertModal = ({ expert, onClose }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { getExpertRating } = useExpertContext();
  const rating = getExpertRating(expert.id);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }} 
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl"
        >
          {/* Header */}
          <div className="p-8 pb-4 flex items-start justify-between">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiUser} className="text-white text-2xl" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-black text-slate-900">{expert.full_name}</h2>
                  {rating.count > 0 && (
                    <div className="flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-lg border border-amber-100">
                      <SafeIcon icon={FiStar} className="text-xs text-amber-500 fill-amber-500" />
                      <span className="text-xs font-black text-amber-700">{rating.avg}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 text-slate-400 font-bold text-xs uppercase tracking-widest mt-1">
                  <SafeIcon icon={FiMapPin} className="text-green-500" />
                  <span>{expert.governorate}</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="text-xl text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-8 pt-4 space-y-6">
            <p className="text-slate-600 font-medium leading-relaxed line-clamp-3">
              {expert.bio || 'No bio provided.'}
            </p>

            <div className="flex flex-wrap gap-2">
              {expert.support_topics?.slice(0, 3).map((topic, index) => (
                <span key={index} className="px-3 py-1 bg-slate-50 text-slate-500 rounded-lg text-[10px] font-black border border-slate-100 uppercase tracking-wider">
                  {topic}
                </span>
              ))}
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => {
                  onClose();
                  navigate(`/expert/${expert.id}`);
                }}
                className="flex-1 bg-slate-900 text-white py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-slate-800 transition-all shadow-xl shadow-slate-200"
              >
                View Full Profile
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ExpertModal;